"use client";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Link from "next/link";

import NavPanelItems from "@/components/nav/NavPanelItems";
import {
  NAV_ARRIVE,
  NAV_CORAL_MARK,
  NAV_CORAL_TEXT,
  NAV_EYEBROW_TYPE,
  NAV_NOTE,
  NAV_SCROLLBAR,
  arriveStep,
} from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

/**
 * What sits beside the icon rail, at `width` while open: it animates to zero
 * width when hidden and is then inert, so its contents leave the tab order
 * with it. The menu panel, the search pane and Sabine's chat all open in it,
 * and all move the same way (`.nav-slot` in app/globals.css): the pieces
 * inside that carry NAV_ARRIVE fade out as it closes and come in, in order,
 * as it opens.
 * It draws no edge of its own: it sits in the cream column SideNav lays
 * around it (`.nav-column`), whose rounded corner meets the navy frame and
 * the sheet. `label` names the shell as a region for assistive tech when
 * nothing inside it does.
 */
export function NavPanelShell({ open, width = "w-64", label, children }) {
  return (
    <div
      role={label ? "region" : undefined}
      aria-label={label}
      aria-hidden={!open}
      inert={!open}
      data-open={open}
      className={cn(
        "nav-slot flex min-h-0 flex-col overflow-hidden",
        open ? width : "w-0",
      )}
    >
      <div className={cn("flex h-full flex-col", width)}>{children}</div>
    </div>
  );
}

const CRUMB_BUTTON =
  "shrink-0 rounded uppercase transition-colors hover:text-gray-900";

const Crumb = ({ label, name, onClick }) => (
  <button type="button" aria-label={name} onClick={onClick} className={CRUMB_BUTTON}>
    {label}
  </button>
);

const Separator = () => (
  <ChevronRightIcon aria-hidden="true" className="h-3 w-3 shrink-0 text-gray-300" />
);

/**
 * The section panel beside the icon rail: the links of the rail's selected
 * menu, or the open page's outline, under a header naming the section (the
 * studio is named by the band above).
 *
 * `view` is `{ title, subtitle?, items, activeHrefs?, crossLink?, parent?,
 * child? }`, built by SideNav from a manifest menu or a page's registered
 * outline. An outline's `parent` is the menu it stands in for: the header
 * reads "Learn › Playbook", and the menu's name hands the panel back to it
 * through `onSelectMenu`. That menu, shown while the page is still open, has
 * the outline as its `child`: the header keeps reading "Learn › Playbook",
 * and the outline's name hands the panel forward again, so the trail is
 * never lost while the page has one.
 *
 * A view may also bring `lead` (a control above its links), `empty` (what
 * to say when it has none) and `rowAction` (a button at the end of each
 * link): the Bookmarks view has all three, and menus carry a link's star
 * (NavBookmarks).
 */
export default function NavPanel({
  open,
  view,
  topAction,
  pathname,
  currentHash,
  onNavigateHash,
  onSelectMenu,
}) {
  return (
    <NavPanelShell open={open}>
      <div className={cn(NAV_ARRIVE, "px-4 pb-1 pt-4")} style={arriveStep(0)}>
        <div className={cn("flex items-center gap-1.5", NAV_EYEBROW_TYPE)}>
          {view.parent && (
            <>
              <Crumb
                label={view.parent.label}
                name={`Back to ${view.parent.label}`}
                onClick={() => onSelectMenu?.(view.parent.key)}
              />
              <Separator />
            </>
          )}
          <span className="truncate text-gray-500">{view.title}</span>
          {view.child && (
            <>
              <Separator />
              <Crumb
                label={view.child.label}
                name={`Open ${view.child.label}`}
                onClick={() => onSelectMenu?.(view.child.key)}
              />
            </>
          )}
        </div>
        {view.subtitle && (
          <div className="mt-0.5 text-xs text-gray-500">{view.subtitle}</div>
        )}
      </div>

      <nav
        aria-label={`${view.title} menu`}
        className={cn(
          NAV_ARRIVE,
          "flex-1 overflow-y-auto px-2 pb-3 pt-2",
          NAV_SCROLLBAR,
        )}
        style={arriveStep(1)}
      >
        {topAction && (
          <div className="mb-2 border-b border-stone-200/70 pb-3">
            <div className="px-3">{topAction}</div>
          </div>
        )}
        {view.lead}
        {view.empty && view.items.length === 0 && (
          <p className={NAV_NOTE}>{view.empty}</p>
        )}
        <NavPanelItems
          items={view.items}
          activeHrefs={view.activeHrefs}
          pathname={pathname}
          currentHash={currentHash}
          onNavigateHash={onNavigateHash}
          rowAction={view.rowAction}
        />
      </nav>

      {view.crossLink && (
        <div
          className={cn(NAV_ARRIVE, "border-t border-stone-200/70 p-2")}
          style={arriveStep(2)}
        >
          <Link
            href={view.crossLink.href}
            className="flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-xs text-gray-500 transition-colors hover:bg-stone-200/50 hover:text-gray-900"
          >
            <span>{view.crossLink.prompt}</span>
            <span
              className={cn(
                "flex shrink-0 items-center gap-0.5 font-semibold",
                NAV_CORAL_TEXT,
              )}
            >
              {view.crossLink.label}
              <ChevronRightIcon className={cn("h-3.5 w-3.5", NAV_CORAL_MARK)} />
            </span>
          </Link>
        </div>
      )}
    </NavPanelShell>
  );
}
